import {
  Button,
  Card,
  FlexButton,
  IconWrapper,
  Loader,
  Tag,
} from "@caprikorn/dynamo-ui";
import { classNames, getFullName } from "@caprikorn/dynamo-utils";
import { useState } from "react";
import type { FC } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { ErrorTemplate } from "@components/ErrorTemplate/ErrorTemplate";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { AddPlusCircle, LayerTriple, Star, TrashEmpty, User03 } from "@icons";
import { ROUTES } from "@router/router.constants";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useGetUsers } from "@services/apiEcms/useGetUsers";
import { formatCaughtError } from "@utils/error.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import type { ExtendedUser } from "../AccessManager.types";
import classes from "./Users.module.css";
import { AccessErrors } from "../AccessErrors/AccessErrors";
import { DeleteUserModal } from "../DeleteUserModal/DeleteUserModal";

export const Users: FC = () => {
  const { t } = useCustomTranslation("main");
  const navigate = useNavigate();
  const { currentGroup } = useGroupReferences();
  const [userToDelete, setUserToDelete] = useState<ExtendedUser>();
  const { data: isGroupAdmin } = useGetGroup({
    groupId: currentGroup,
    options: { select: (res) => res.isAdmin },
  });

  const {
    data: users,
    isSuccess: isUsersSuccess,
    isLoading: isUsersLoading,
    refetch: refetchUsers,
    error: usersError,
  } = useGetUsers(isGroupAdmin);

  if (isUsersLoading) {
    return <Loader size="lg" />;
  }

  if (usersError || !isGroupAdmin) {
    return (
      <div className={classes.accessErrors}>
        <AccessErrors
          errorStatus={
            !isGroupAdmin ? 403 : formatCaughtError(usersError).status
          }
          location="list"
          refetch={refetchUsers}
        />
      </div>
    );
  }

  if (isUsersSuccess && users.length === 0) {
    return (
      <div className={classes.emptyUserList}>
        <ErrorTemplate
          imageType="incompatible"
          text={t("userDetails.list.emptyList.text")}
          title={t("userDetails.list.emptyList.title")}
        />
        <Button
          data-testid="EmptyUsersList-AddUser"
          onClick={() => navigate(ROUTES.ACCESS_MANAGER_CREATE)}
          startIcon={AddPlusCircle}
        >
          {t("userDetails.list.addCollaborator")}
        </Button>
      </div>
    );
  }

  return (
    <>
      {userToDelete && (
        <DeleteUserModal
          onClose={() => setUserToDelete(undefined)}
          user={userToDelete}
        />
      )}
      <div className={classes.container}>
        {users && (
          <span data-testid="Users-UserNumber">
            {t("userDetails.list.collaborator", {
              count: users.length,
            })}
          </span>
        )}
        <div className={classes.grid}>
          <Button
            className={classes.addButton}
            data-testid="Users-Card-AddUser"
            onClick={() => navigate(ROUTES.ACCESS_MANAGER_CREATE)}
            startIcon={AddPlusCircle}
            variant="secondary"
          >
            {t("userDetails.list.addCollaborator")}
          </Button>
          {users?.map((user) => (
            <Card
              key={user.id}
              aria-label={t("userDetails.list.collaboratorCard")}
              as="section"
              className={classes.userCard}
              data-testid="AccessManager-UserCard"
              onClick={() =>
                navigate(
                  generatePath(ROUTES.ACCESS_MANAGER_USER, {
                    userId: user.id,
                  }),
                )
              }
            >
              <IconWrapper role="presentation" size="lg" src={User03} />
              <div className={classes.column}>
                <div className={classes.titleBar}>
                  <div className={classNames(classes.title, "font-title-md")}>
                    {getFullName(user) ||
                      t("userDetails.informations.unknownIdentity")}
                  </div>
                  <FlexButton
                    aria-label={t("userDetails.list.deleteCollaborator")}
                    className={classes.deleteButton}
                    data-testid="PictoAsButton-DeleteUser"
                    icon={TrashEmpty}
                    onClick={(event) => {
                      event.stopPropagation();
                      setUserToDelete(user);
                    }}
                  />
                </div>
                <div className={classes.email}>{user.email}</div>
                <span className="font-caption">
                  {t("userDetails.list.createdAt", {
                    date: user.createdAt && new Date(user.createdAt),
                  })}
                </span>
                <div className={classes.row}>
                  {user.isAdmin && (
                    <Tag
                      color="contrast"
                      icon={Star}
                      iconStyle="start"
                      label={t("userDetails.role.labels.admin")}
                      size="xs"
                    />
                  )}
                  <Tag
                    icon={LayerTriple}
                    iconStyle="start"
                    label={t("userDetails.list.customer", {
                      count: user.numberOfCustomerIds,
                    })}
                    size="xs"
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </>
  );
};
